import React, { useState, useEffect } from 'react';
import axios from '../axios-config';
import { CheckCircle2, FileCheck2, Banknote, AlertTriangle, Lock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SUPPLIER V1 · Phase B — Module 11 (Work Completion) + Module 9 (Award notification) + PO Gate UI
 * Renders only for the awarded supplier of a tender. Drops into TenderDetails.
 */
export default function SupplierAwardWorkflow({ tender, refresh }) {
  const [highRiskCats, setHighRiskCats] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [completionDate, setCompletionDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [disRes, invRes] = await Promise.all([
          axios.get(`${API}/disclaimers`),
          axios.get(`${API}/supplier/success-fee-invoices`).catch(() => ({ data: [] })),
        ]);
        setHighRiskCats(disRes.data?.high_risk_categories || []);
        const invs = (invRes.data || []).filter(i => i.tender_id === tender.id);
        setInvoice(invs[0] || null);
      } catch (e) { /* silent */ }
    })();
  }, [tender.id]);

  const isHighRisk = highRiskCats.includes(tender.work_category);
  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

  const confirmWork = async () => {
    if (!completionDate) { toast.error('Please pick a completion date'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/supplier/tenders/${tender.id}/work-confirm`, { completion_date: completionDate, notes });
      toast.success('Work confirmation recorded');
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const ackNOC = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/supplier/tenders/${tender.id}/noc-acknowledge`, {});
      toast.success('NOC acknowledged');
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const ackPayment = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/supplier/tenders/${tender.id}/payment-acknowledge`, {});
      toast.success('Final payment acknowledged');
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 mt-6" data-testid="supplier-award-workflow">
      {/* Award notification */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-5" data-testid="supplier-award-banner">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-base font-bold text-green-900">🎉 Congratulations — You've been awarded this tender!</div>
            <div className="text-sm text-green-800 mt-0.5">Awarded amount: <strong>{fmt(tender.awarded_amount)}</strong> · Status: <span className="font-semibold uppercase">{tender.status?.replace(/_/g, ' ')}</span></div>
          </div>
        </div>
      </div>

      {/* High-risk category warning (Disclaimer C) */}
      {isHighRisk && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 text-xs text-red-900" data-testid="supplier-highrisk-warning">
          <AlertTriangle className="inline h-4 w-4 mr-1 -mt-0.5" />
          <strong>High-Risk Category Notice ({tender.work_category}):</strong> Buyer is advised to verify all applicable licenses, certifications and statutory approvals before awarding work. Please ensure your statutory compliance is up to date.
        </div>
      )}

      {/* Success Fee + PO Gate */}
      {invoice && (
        <div className={`border-2 rounded-lg p-4 ${invoice.status === 'paid' ? 'border-green-300 bg-green-50/30' : 'border-orange/40 bg-orange/5'}`} data-testid="supplier-success-fee-card">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wide">Platform Success Fee</div>
              <div className="text-sm font-bold text-navy">{invoice.invoice_number}</div>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-orange/20 text-orange'}`} data-testid="supplier-success-fee-status">
              {invoice.status === 'paid' ? 'PAID — PO Unlocked' : 'PENDING'}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[10px] mb-3">
            <Stat label="Contract" value={fmt(invoice.contract_value)} />
            <Stat label={`Fee ${invoice.fee_percentage}%`} value={fmt(invoice.fee_amount)} />
            <Stat label={`GST ${invoice.gst_percentage}%`} value={fmt(invoice.gst_amount)} />
            <Stat label="Total" value={fmt(invoice.total_amount)} highlight />
          </div>
          {invoice.status !== 'paid' && (
            <a href="/supplier-dashboard/success-fees" className="w-full px-4 py-2.5 rounded bg-orange text-white text-sm font-bold inline-flex items-center justify-center" data-testid="supplier-go-to-success-fees-btn">
              <Lock className="h-4 w-4 mr-2" /> Pay to Unlock PO
            </a>
          )}
          {invoice.status === 'paid' && tender.purchase_order?.po_number && (
            <div className="text-xs text-green-800 inline-flex items-center font-semibold" data-testid="supplier-po-unlocked-info">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> PO {tender.purchase_order.po_number} unlocked — see <a href="/supplier-dashboard/success-fees" className="underline ml-1 inline-flex items-center">Success Fees <ExternalLink className="h-3 w-3 ml-0.5" /></a>
            </div>
          )}
        </div>
      )}

      {/* Module 11 — Work Confirmation actions (only after PO + fee paid) */}
      {tender.purchase_order && invoice?.status === 'paid' && (
        <div className="border border-border bg-card rounded-lg p-4 space-y-3" data-testid="supplier-work-confirm-card">
          <h3 className="text-sm font-bold text-navy inline-flex items-center"><FileCheck2 className="h-4 w-4 mr-1.5 text-navy" /> Confirm Work Completion</h3>
          {tender.supplier_work_confirmed ? (
            <div className="text-xs text-green-700 font-semibold inline-flex items-center" data-testid="supplier-work-confirmed-info">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Work confirmed on {tender.supplier_work_confirmed.completion_date}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">Completion Date</label>
                  <input type="date" value={completionDate} onChange={e => setCompletionDate(e.target.value)} className="w-full px-3 py-2 rounded border border-input bg-background text-sm" data-testid="supplier-work-completion-date" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">Notes (optional)</label>
                  <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2 rounded border border-input bg-background text-sm" placeholder="e.g., delivered as per scope" data-testid="supplier-work-notes" />
                </div>
              </div>
              <button onClick={confirmWork} disabled={busy} className="w-full px-4 py-2.5 rounded bg-navy text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center" data-testid="supplier-work-confirm-btn">
                <CheckCircle2 className="h-4 w-4 mr-2" /> {busy ? 'Saving…' : 'Confirm Work Completion'}
              </button>
            </>
          )}
        </div>
      )}

      {/* NOC acknowledgment */}
      {tender.noc && (
        <div className="border border-border bg-card rounded-lg p-4" data-testid="supplier-noc-card">
          <h3 className="text-sm font-bold text-navy mb-2 inline-flex items-center"><FileCheck2 className="h-4 w-4 mr-1.5" /> No-Objection Certificate</h3>
          {tender.supplier_noc_acknowledged_at ? (
            <div className="text-xs text-green-700 font-semibold inline-flex items-center" data-testid="supplier-noc-acked-info">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> NOC acknowledged on {new Date(tender.supplier_noc_acknowledged_at).toLocaleDateString()}
            </div>
          ) : (
            <button onClick={ackNOC} disabled={busy} className="w-full px-4 py-2.5 rounded border-2 border-navy text-navy text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center hover:bg-navy hover:text-white transition-colors" data-testid="supplier-noc-ack-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Acknowledge NOC
            </button>
          )}
        </div>
      )}

      {/* Final Payment acknowledgment */}
      {tender.final_payment && (
        <div className="border border-border bg-card rounded-lg p-4" data-testid="supplier-payment-card">
          <h3 className="text-sm font-bold text-navy mb-2 inline-flex items-center"><Banknote className="h-4 w-4 mr-1.5" /> Final Payment</h3>
          {tender.supplier_payment_acknowledged_at ? (
            <div className="text-xs text-green-700 font-semibold inline-flex items-center" data-testid="supplier-payment-acked-info">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Payment acknowledged on {new Date(tender.supplier_payment_acknowledged_at).toLocaleDateString()}
            </div>
          ) : (
            <button onClick={ackPayment} disabled={busy} className="w-full px-4 py-2.5 rounded bg-green-600 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center" data-testid="supplier-payment-ack-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Acknowledge Final Payment Received
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div className={`p-1.5 rounded ${highlight ? 'bg-navy text-white' : 'bg-card border border-border'}`}>
    <div className={`text-[8px] uppercase font-bold tracking-wide ${highlight ? 'text-gold' : 'text-muted-foreground'}`}>{label}</div>
    <div className={`text-[11px] font-bold ${highlight ? 'text-white' : 'text-navy'}`}>{value}</div>
  </div>
);
